import React, { useState } from 'react';
import styled from 'styled-components';

// 全体のコンテナスタイル
const ForecastRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff; /* 背景色を白に変更 */
  color: #333333; /* テキストカラーを濃い灰色に */
  border-radius: 15px; /* 角を丸く */
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* 影を追加して浮かび上がる効果 */
  text-align: center;
  transition: transform 0.2s;
  width: 100%; /* 幅を100%に */
  box-sizing: border-box; /* ボックスサイズを調整 */
  &:hover {
    transform: scale(1.02); /* ホバー時に少し拡大 */
  }
`;

const ColumnHeader = styled.div`
  font-weight: bold;
  font-size: 1.2rem;
  color: #198951; /* 見出しの色を統一 */
  margin-bottom: 10px;
`;

// 矢印アイコンのコンテナ  
const ArrowIcon = styled.span`  
  margin-top: 10px;
  font-size: 1.2rem;  
  transition: transform 0.3s;  
  ${({ isExpanded }) => isExpanded && `  
    transform: rotate(180deg);  
  `}  
`;  

// テーブルスタイル
const ForecastTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: #f8f8f8; /* テーブル背景色を白に近い色に */
  margin-top: 10px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 0.8rem; /* スマホ対応のフォントサイズ */
    table-layout: fixed; /* テーブル列の幅を固定 */
  }
`;

const ForecastHeader = styled.th`
  background-color: #198951; /* ヘッダーの背景色を統一 */
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center;
  color: white;
  font-size: 1rem;
  word-wrap: break-word; /* 単語の折り返しを有効に */
`;

const ForecastCell = styled.td`
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center;
  font-size: 0.9rem;
  color: #333333; /* テキストカラーを濃い灰色に */
  word-wrap: break-word; /* テキストが折り返されるように */
  
  @media (max-width: 768px) {
    padding: 4px; /* スマホ対応のパディング */
  }
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  img {
    width: 40px;
    height: 40px;
  }
  span {
    margin-top: 5px;
    font-size: 0.9rem;
    color: #666666; /* テキストカラーをやや落ち着いた色に */
  }

  @media (max-width: 768px) {
    img {
      width: 30px; /* スマホ対応の画像サイズ */
      height: 30px;
    }
    span {
      font-size: 0.8rem; /* スマホ対応のフォントサイズ */
    }
  }
`;

const ExpandableContent = styled.div`  
  max-height: ${({ isExpanded }) => (isExpanded ? '1000px' : '0')};  
  overflow: hidden;  
  transition: max-height 0.5s ease;  
`;

const iconMapping = {
  1: 'sunny-i.png',
  2: 'mostly-sunny.png',
  3: 'partly-sunny.png',
  4: 'intermittent-clouds.png',
  5: 'hazy-sunshine.png',
  6: 'mostly-cloudy.png',
  7: 'cloudy-i.png',
  8: 'dreary-overcast.png',
  11: 'fog-i.png',
  12: 'showers-i.png',
  13: 'mostly-cloudy-showers.png',
  14: 'partly-sunny-showers.png',
  15: 'tstorms-i.png',
  16: 'mostly-cloudy-tstorms.png',
  17: 'partly-sunny-tstorms.png',
  18: 'rain-i.png',
  19: 'flurries-i.png',
  20: 'mostly-cloudy-flurries.png',
  21: 'partly-sunny-flurries.png',
  22: 'snow-i.png',
  23: 'mostly-cloudy-snow.png',
  24: 'ice-i.png',
  25: 'sleet-i.png',
  26: 'freezing-rain.png',
  29: 'rain-snow.png',
  30: 'hot-i.png',
  31: 'cold-i.png',
  32: 'windy-i.png',
  33: 'clear-night.png',
  34: 'mostly-clear-night.png',
  35: 'partly-cloudy-night.png',
  36: 'intermittent-clouds-night.png',
  37: 'hazymoon-light.png',
  38: 'mostly-cloudy-night.png',
  39: 'partly-cloudy-showers-night.png',
  40: 'mostly-cloudy-showers-night.png',
  41: 'partly-cloudy-tstorms-night.png',
  42: 'mostly-cloudy-tstorms-night.png',
  43: 'mostly-cloudy-flurries-night.png',
  44: 'mostly-cloudy-snow-night.png',
};

const getIconUrl = (iconCode) => `/icons/${iconMapping[iconCode]}`;

const formatDateWithWeekday = (dateString) => {
  const date = new Date(dateString);
  const optionsDate = { month: 'numeric', day: 'numeric' };
  const optionsWeekday = { weekday: 'short' };
  const formattedDate = date.toLocaleDateString('ja-JP', optionsDate);
  const formattedWeekday = date.toLocaleDateString('ja-JP', optionsWeekday);
  return { formattedDate, formattedWeekday };
};

const ForecastCard = ({ forecasts, summary }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const phrase = summary.phrase;
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div onClick={handleToggle}>
      <ForecastRowContainer>
        <ColumnHeader>{phrase}</ColumnHeader>
        <ForecastTable>
          <thead>
            <tr>
              <ForecastHeader>日付</ForecastHeader>
              {forecasts.map((forecast) => (
                <ForecastHeader key={forecast.date}>
                  {formatDateWithWeekday(forecast.date).formattedWeekday}<br />
                  {formatDateWithWeekday(forecast.date).formattedDate}
                </ForecastHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <ForecastCell>昼</ForecastCell>
              {forecasts.map((forecast) => (
                <ForecastCell key={forecast.date}>
                  <IconContainer>
                    <img
                      src={getIconUrl(forecast.day.iconCode)}
                      alt={forecast.day.iconPhrase}
                    />
                    {forecast.day.precipitationProbability}%
                    {/* <span>{forecast.day.shortPhrase}</span> */}
                  </IconContainer>
                </ForecastCell>
              ))}
            </tr>
            <tr>
              <ForecastCell>夜</ForecastCell>
              {forecasts.map((forecast) => (
                <ForecastCell key={forecast.date}>
                  <IconContainer>
                    <img
                      src={getIconUrl(forecast.night.iconCode)}
                      alt={forecast.night.iconPhrase}
                    />
                    {forecast.night.precipitationProbability}%
                    {/* <span>{forecast.night.shortPhrase}</span> */}
                  </IconContainer>
                </ForecastCell>
              ))}
            </tr>
          </tbody>
        </ForecastTable>
        <ArrowIcon isExpanded={isExpanded}>▼</ArrowIcon>
      </ForecastRowContainer>

      {/* 気温 */}
      <ExpandableContent isExpanded={isExpanded}>
        <ForecastRowContainer>
          <ColumnHeader>気温</ColumnHeader>
          <ForecastTable>
            <thead>
              <tr>
                <ForecastHeader>日付</ForecastHeader>
                {forecasts.map((forecast) => (
                  <ForecastHeader key={forecast.date}>
                    {formatDateWithWeekday(forecast.date).formattedWeekday}<br />
                    {formatDateWithWeekday(forecast.date).formattedDate}
                  </ForecastHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <ForecastCell>最低</ForecastCell>
                {forecasts.map((forecast) => (
                  <ForecastCell key={forecast.date}>
                    {forecast.temperature.minimum.value}°C
                  </ForecastCell>
                ))}
              </tr>
              <tr>
                <ForecastCell>最高</ForecastCell>
                {forecasts.map((forecast) => (
                  <ForecastCell key={forecast.date}>
                    {forecast.temperature.maximum.value}°C
                  </ForecastCell>
                ))}
              </tr>
            </tbody>
          </ForecastTable>
          <ArrowIcon isExpanded={isExpanded}>▼</ArrowIcon>
        </ForecastRowContainer>
      </ExpandableContent>

      {/* 体感温度 */}
      <ExpandableContent isExpanded={isExpanded}>
        <ForecastRowContainer>
          <ColumnHeader>体感温度</ColumnHeader>
          <ForecastTable>
            <thead>
              <tr>
                <ForecastHeader>日付</ForecastHeader>
                {forecasts.map((forecast) => (
                  <ForecastHeader key={forecast.date}>
                    {formatDateWithWeekday(forecast.date).formattedWeekday}<br />
                    {formatDateWithWeekday(forecast.date).formattedDate}
                  </ForecastHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <ForecastCell>昼</ForecastCell>
                {forecasts.map((forecast) => (
                  <ForecastCell key={forecast.date}>
                    {forecast.realFeelTemperature.maximum.value}°C
                  </ForecastCell>
                ))}
              </tr>
              <tr>
                <ForecastCell>夜</ForecastCell>
                {forecasts.map((forecast) => (
                  <ForecastCell key={forecast.date}>
                    {forecast.realFeelTemperature.minimum.value}°C
                  </ForecastCell>
                ))}
              </tr>
            </tbody>
          </ForecastTable>
          <ArrowIcon isExpanded={isExpanded}>▼</ArrowIcon>
        </ForecastRowContainer>
      </ExpandableContent>

      {/* 風速 */}
      <ExpandableContent isExpanded={isExpanded}>
        <ForecastRowContainer>
          <ColumnHeader>風速</ColumnHeader>
          <ForecastTable>
            <thead>
              <tr>
                <ForecastHeader>日付</ForecastHeader>
                {forecasts.map((forecast) => (
                  <ForecastHeader key={forecast.date}>
                    {formatDateWithWeekday(forecast.date).formattedWeekday}<br />
                    {formatDateWithWeekday(forecast.date).formattedDate}
                  </ForecastHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <ForecastCell>昼</ForecastCell>
                {forecasts.map((forecast) => (
                  <ForecastCell key={forecast.date}>
                    {forecast.day.wind.speed.value} km/h (
                    {forecast.day.wind.direction.localizedDescription})
                  </ForecastCell>
                ))}
              </tr>
              <tr>
                <ForecastCell>夜</ForecastCell>
                {forecasts.map((forecast) => (
                  <ForecastCell key={forecast.date}>
                    {forecast.night.wind.speed.value} km/h (
                    {forecast.night.wind.direction.localizedDescription})
                  </ForecastCell>
                ))}
              </tr>
            </tbody>
          </ForecastTable>
          <ArrowIcon isExpanded={isExpanded}>▼</ArrowIcon>
        </ForecastRowContainer>
      </ExpandableContent>

      {/* 日照時間 */}
      <ExpandableContent isExpanded={isExpanded}>
        <ForecastRowContainer>
          <ColumnHeader>日照時間</ColumnHeader>
          <ForecastTable>
            <thead>
              <tr>
                <ForecastHeader>日付</ForecastHeader>
                {forecasts.map((forecast) => (
                  <ForecastHeader key={forecast.date}>
                    {formatDateWithWeekday(forecast.date).formattedWeekday}<br />
                    {formatDateWithWeekday(forecast.date).formattedDate}
                  </ForecastHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <ForecastCell>日照時間</ForecastCell>
                {forecasts.map((forecast) => (
                  <ForecastCell key={forecast.date}>
                    {forecast.hoursOfSun} 時間
                  </ForecastCell>
                ))}
              </tr>
            </tbody>
          </ForecastTable>
          <ArrowIcon isExpanded={isExpanded}>▼</ArrowIcon>
        </ForecastRowContainer>
      </ExpandableContent>

      {/* UV指数 */}
      <ExpandableContent isExpanded={isExpanded}>
        <ForecastRowContainer>
          <ColumnHeader>UV指数</ColumnHeader>
          <ForecastTable>
            <thead>
              <tr>
                <ForecastHeader>日付</ForecastHeader>
                {forecasts.map((forecast) => (
                  <ForecastHeader key={forecast.date}>
                    {formatDateWithWeekday(forecast.date).formattedWeekday}<br />
                    {formatDateWithWeekday(forecast.date).formattedDate}
                  </ForecastHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <ForecastCell>UV指数</ForecastCell>
                {forecasts.map((forecast) => (
                  <ForecastCell key={forecast.date}>
                    {
                      forecast.airAndPollen.find(
                        (p) => p.name === 'UVIndex'
                      ).value
                    }
                  </ForecastCell>
                ))}
              </tr>
            </tbody>
          </ForecastTable>
          <ArrowIcon isExpanded={isExpanded}>▼</ArrowIcon>
        </ForecastRowContainer>
      </ExpandableContent>
    </div>
  );
};

export default ForecastCard;